/**
 * LoginPage Component
 *
 * Authentication page with email/password form. Split-pane layout:
 * editorial dark-mode left panel sets the brand tone, clean right
 * panel hosts the form. Handles login submission, error display,
 * rate limiting, and role-based redirect after success.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth, getDashboardPath } from '../contexts/AuthContext';
import { useRateLimitTimer } from '../hooks/useRateLimitTimer';

interface LocationState {
  from?: { pathname: string };
}

export const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated, user, isLoading } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { isRateLimited, secondsRemaining, handleError: handleRateLimitError } = useRateLimitTimer();

  const locationState = location.state as LocationState | null;
  const from = locationState?.from?.pathname;

  useEffect(() => {
    if (isAuthenticated && user && !isLoading) {
      if (user.mustChangePassword) {
        navigate('/change-password', { replace: true });
        return;
      }
      const destination = from || getDashboardPath(user.role);
      navigate(destination, { replace: true });
    }
  }, [isAuthenticated, user, isLoading, navigate, from]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (isRateLimited) return;

    if (!email.trim()) {
      setError('Email is required');
      return;
    }
    if (!password) {
      setError('Password is required');
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await login(email.trim(), password);
      if (result.success && result.user) {
        if (result.user.mustChangePassword) {
          navigate('/change-password', { replace: true });
          return;
        }
        const destination = from || getDashboardPath(result.user.role);
        navigate(destination, { replace: true });
      } else if (result.requiresTwoFactor) {
        setError('Two-factor authentication is not yet supported');
      } else {
        const errorMsg = handleRateLimitError({ message: result.error, code: result.code });
        setError(errorMsg || 'Invalid email or password');
      }
    } catch (err) {
      const errorMsg = handleRateLimitError(err);
      setError(errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          gap: 'var(--space-4)',
          backgroundColor: 'var(--bg)',
        }}
      >
        <div
          style={{
            width: '36px',
            height: '36px',
            border: '2px solid var(--line-strong)',
            borderTopColor: 'var(--accent)',
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite',
          }}
        />
      </div>
    );
  }

  return (
    <main
      style={{
        minHeight: '100vh',
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        position: 'relative',
        zIndex: 2,
      }}
      className="login-shell"
    >
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @media (max-width: 900px) {
          .login-shell { grid-template-columns: 1fr !important; }
          .login-aside { display: none !important; }
          .login-form-wrap { padding: var(--space-8) !important; }
        }
      `}</style>

      {/* Left aside — editorial dark panel */}
      <aside
        className="login-aside"
        style={{
          background: 'var(--ink)',
          color: 'white',
          padding: 'var(--space-12) var(--space-8)',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            inset: '-200px',
            background:
              'radial-gradient(circle at 20% 30%, rgba(200, 65, 26, 0.4), transparent 50%), radial-gradient(circle at 80% 70%, rgba(212, 165, 82, 0.18), transparent 50%)',
          }}
        />

        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'center', marginBottom: 'var(--space-8)' }}>
            <span
              className="eyebrow eyebrow-plain"
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: '0.7rem',
                color: 'rgba(255,255,255,0.55)',
                letterSpacing: '0.16em',
                textTransform: 'uppercase',
              }}
            >
              WEBGEN-ENGINE · v2.0
            </span>
            <span
              className="sys-status"
              style={{
                background: 'rgba(255,255,255,0.06)',
                border: '1px solid rgba(255,255,255,0.12)',
                color: 'rgba(255,255,255,0.7)',
              }}
            >
              <span className="d" />
              Operational
            </span>
          </div>

          <h1
            style={{
              fontFamily: 'var(--font-body)',
              fontWeight: 600,
              fontSize: 'clamp(2.5rem, 4vw, 3.5rem)',
              letterSpacing: '-0.035em',
              lineHeight: 1.05,
              color: 'white',
              margin: 0,
            }}
          >
            Ship{' '}
            <span
              className="display"
              style={{
                color: '#d4a552',
                fontFamily: 'var(--font-display)',
                fontWeight: 400,
                fontStyle: 'italic',
                letterSpacing: '-0.025em',
              }}
            >
              compliant
            </span>{' '}
            websites in under five minutes.
          </h1>

          <p
            style={{
              marginTop: 'var(--space-6)',
              color: 'rgba(255,255,255,0.65)',
              maxWidth: '48ch',
              fontSize: 'var(--text-base)',
              lineHeight: 1.55,
            }}
          >
            Production-ready websites, generated on demand.
          </p>
        </div>

        <div style={{ position: 'relative', zIndex: 1 }}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: 'var(--space-4)',
              paddingTop: 'var(--space-6)',
              borderTop: '1px solid rgba(255,255,255,0.12)',
              marginBottom: 'var(--space-6)',
            }}
          >
            {[
              { label: 'Compliant', detail: 'Google Ads policy + GDPR baked in' },
              { label: 'Configurable', detail: 'Pick style, brand, language, region' },
              { label: 'Delivered', detail: 'Production-ready ZIP in one click' },
            ].map((s) => (
              <div key={s.label}>
                <div
                  style={{
                    fontFamily: 'var(--font-mono)',
                    fontSize: '0.7rem',
                    color: '#d4a552',
                    letterSpacing: '0.12em',
                    textTransform: 'uppercase',
                    fontWeight: 500,
                  }}
                >
                  {s.label}
                </div>
                <div
                  style={{
                    marginTop: 8,
                    fontSize: 'var(--text-sm)',
                    color: 'rgba(255,255,255,0.72)',
                    lineHeight: 1.4,
                  }}
                >
                  {s.detail}
                </div>
              </div>
            ))}
          </div>
          <div
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 'var(--text-xs)',
              color: 'rgba(255,255,255,0.4)',
              letterSpacing: '0.05em',
            }}
          >
            © {new Date().getFullYear()} WebGen Engine
          </div>
        </div>
      </aside>

      {/* Right side — form */}
      <section
        className="login-form-wrap"
        style={{
          padding: 'var(--space-12)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <form
          onSubmit={handleSubmit}
          className="fade-in"
          style={{
            width: '100%',
            maxWidth: '380px',
          }}
        >
          <span className="eyebrow eyebrow-accent">AUTH · SIGN IN</span>
          <h2
            style={{
              marginTop: 'var(--space-3)',
              fontSize: 'var(--text-3xl)',
              fontWeight: 600,
              letterSpacing: '-0.03em',
            }}
          >
            Sign in to WebGen.
          </h2>
          <p
            style={{
              color: 'var(--ink-muted)',
              fontFamily: 'var(--font-mono)',
              fontSize: '0.8rem',
              letterSpacing: '0.01em',
              marginTop: 'var(--space-3)',
              marginBottom: 'var(--space-8)',
            }}
          >
            Use your company email and the password your manager provisioned.
          </p>

          {(error || isRateLimited) && (
            <div
              role="alert"
              style={{
                padding: 'var(--space-3) var(--space-4)',
                backgroundColor: isRateLimited ? 'var(--warning-bg)' : 'var(--danger-bg)',
                border: `1px solid ${isRateLimited ? 'var(--warning)' : 'var(--color-error)'}`,
                borderRadius: 'var(--radius-md)',
                marginBottom: 'var(--space-5)',
                fontFamily: 'var(--font-body)',
                fontSize: 'var(--text-sm)',
                color: isRateLimited ? 'var(--warning)' : 'var(--color-error)',
              }}
            >
              {isRateLimited ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                  <span>Too many attempts. Please wait </span>
                  <span
                    className="kbd"
                    style={{
                      background: 'rgba(0,0,0,0.05)',
                      borderColor: 'rgba(0,0,0,0.1)',
                      color: 'inherit',
                      fontWeight: 600,
                    }}
                  >
                    {secondsRemaining}s
                  </span>
                </div>
              ) : (
                error
              )}
            </div>
          )}

          <div className="form-field" style={{ marginBottom: 'var(--space-5)' }}>
            <label htmlFor="email" className="form-label">
              Work email
            </label>
            <input
              id="email"
              type="email"
              className="input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
              autoComplete="email"
              autoFocus
              disabled={isSubmitting || isRateLimited}
            />
          </div>

          <div className="form-field" style={{ marginBottom: 'var(--space-6)' }}>
            <label htmlFor="password" className="form-label">
              Password
            </label>
            <input
              id="password"
              type="password"
              className="input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••••"
              autoComplete="current-password"
              disabled={isSubmitting || isRateLimited}
            />
          </div>

          <button
            type="submit"
            className={`btn btn-primary btn-lg ${isSubmitting ? 'btn-loading' : ''}`}
            disabled={isSubmitting || isRateLimited}
            style={{ width: '100%' }}
          >
            {isRateLimited
              ? `Wait ${secondsRemaining}s`
              : isSubmitting
                ? 'Signing in…'
                : (
                  <>
                    Sign in
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M5 12h14M12 5l7 7-7 7" />
                    </svg>
                  </>
                )}
          </button>
        </form>
      </section>
    </main>
  );
};

export default LoginPage;
